import React, { useLayoutEffect, useState } from "react";
import Navbar from "../navbar/Navbar";
import Footer from "../Footer/Footer";
import ReCAPTCHA from "react-google-recaptcha";

export default function ContactPage() {
	const [form, setForm] = useState({
		brandName: "Monsini",
		firstName: "",
		email: "",
		phone: "",
		message: "",
	});
	const [errors, setErrors] = useState({});
	const [status, setStatus] = useState(null);
	const [capVal, setCapVal] = useState(null);
	useLayoutEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);
	// --- Form Validation ---
	function validate() {
		const e = {};
		if (!form.firstName.trim()) e.firstName = "First name is required";
		if (!form.email.trim()) e.email = "Email is required";
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
			e.email = "Enter a valid email";
		if (!form.phone.trim()) e.phone = "Phone Number is required";
		if (!form.message.trim()) e.message = "Message is required";
		return e;
	}

	const handleChange = (e) => {
		const { name, value } = e.target;
		setForm((f) => ({ ...f, [name]: value }));
		// Clear error when user starts typing
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const validation = validate();
		setErrors(validation);
		if (Object.keys(validation).length) return;

		// Check if reCAPTCHA is completed
		if (!capVal) {
			setStatus("recaptcha-error");
			return;
		}

		try {
			setStatus("sending");

			// Prepare data for API
			const formData = {
				name: form.firstName,
				email: form.email,
				message: form.message,
				brandName: form.brandName,
				phone: form.phone,
				recaptchaToken: capVal,
			};

			// Send to API endpoint
			const response = await fetch(
				`https://admin.monsinidress.com/api/contact`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(formData),
				}
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const result = await response.json();

			if (result.success) {
				setStatus("success");
				setForm({
					brandName: "Monsini",
					firstName: "",
					email: "",
					message: "",
					phone: "",
				});
				setCapVal(null);
				setTimeout(() => setStatus(null), 5000);
			} else {
				throw new Error(result.message || "Failed to send message");
			}
		} catch (err) {
			console.error("Contact form error:", err);
			setStatus("error");
		}
	};

	// Helper function for input classes to clean up JSX
	const getInputClasses = (field) => {
		const base =
			"mt-1 block w-full rounded-lg border px-4 py-3 shadow-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-zinc-800 bg-white placeholder-gray-400";
		return errors[field]
			? `${base} border-red-500`
			: `${base} border-gray-300 hover:border-gray-400`;
	};

	return (
		<>
			<Navbar />
			<div className="mt-[100px] min-h-screen bg-gray-100 py-20 px-4 sm:px-6 lg:px-10">
				<div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
					{/* Left Section - Contact Form */}
					<section className="bg-white p-10 rounded-xl shadow-2xl border border-gray-200">
						<h1 className="text-4xl font-americana font-bold text-pink-600 mb-2 tracking-wide">
							Contact Us
						</h1>
						<p className="text-gray-600 mb-8 text-lg">
							For inquiries about our bespoke elegant gowns, please reach out.
							We are dedicated to providing a timely response within 24 hours.
						</p>

						<form onSubmit={handleSubmit} className="space-y-6">
							{/* First Name Input */}
							<div>
								<label
									htmlFor="firstName"
									className="block text-sm font-semibold text-gray-700">
									Your First Name
								</label>
								<input
									id="firstName"
									name="firstName"
									value={form.firstName}
									onChange={handleChange}
									type="text"
									placeholder="Enter your first name"
									className={getInputClasses("firstName")}
								/>
								{errors.firstName && (
									<p className="mt-1 text-sm text-red-600 font-medium">
										{errors.firstName}
									</p>
								)}
							</div>

							{/* Email Input */}
							<div>
								<label
									htmlFor="email"
									className="block text-sm font-semibold text-gray-700">
									Your Email Address*
								</label>
								<input
									id="email"
									name="email"
									value={form.email}
									onChange={handleChange}
									type="email"
									placeholder="name@example.com"
									className={getInputClasses("email")}
								/>
								{errors.email && (
									<p className="mt-1 text-sm text-red-600 font-medium">
										{errors.email}
									</p>
								)}
							</div>
							<div>
								<label
									htmlFor="email"
									className="block text-sm font-semibold text-gray-700">
									Your Phone Number
								</label>
								<input
									id="phone"
									name="phone"
									value={form.phone}
									onChange={handleChange}
									type="phone"
									placeholder="(XXX)-XXX-XXX ..."
									className={getInputClasses("phne")}
								/>
								{errors.phone && (
									<p className="mt-1 text-sm text-red-600 font-medium">
										{errors.phone}
									</p>
								)}
							</div>

							{/* Message Textarea */}
							<div>
								<label
									htmlFor="message"
									className="block text-sm font-semibold text-gray-700">
									Your Message*
								</label>
								<textarea
									id="message"
									name="message"
									value={form.message}
									onChange={handleChange}
									rows={6}
									placeholder="Type your message here..."
									className={getInputClasses("message")}
								/>
								{errors.message && (
									<p className="mt-1 text-sm text-red-600 font-medium">
										{errors.message}
									</p>
								)}
							</div>

							{/* reCAPTCHA */}
							<div>
								<ReCAPTCHA
									// sitekey="6LdmLyAsAAAAAEqPIVh3SRYMQI2sDuuPlPxgGusZ"
									sitekey="6LeKeywsAAAAAHhTtElwWALyKKJoy9uXHWODEk4f"
									onChange={(val) => setCapVal(val)}
								/>
								{status === "recaptcha-error" && (
									<p className="mt-2 text-sm text-red-600 font-medium">
										Please complete the reCAPTCHA verification
									</p>
								)}
							</div>

							{/* Submit Button and Status */}
							<div className="pt-2 flex items-center space-x-4">
								<button
									type="submit"
									className="inline-flex items-center text-white justify-center rounded-lg bg-zinc-800 px-6 py-3 text-base font-bold shadow-md hover:bg-zinc-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
									disabled={status === "sending"}>
									{status === "sending" ? (
										<>
											<svg
												className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24">
												<circle
													className="opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													strokeWidth="4"></circle>
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
											</svg>
											Sending...
										</>
									) : (
										"Send Your Inquiry"
									)}
								</button>

								{status === "success" && (
									<p className="text-green-600 font-medium">
										✓ Your inquiry has been sent. Thank you!
									</p>
								)}

								{status === "error" && (
									<p className="text-red-600 font-medium">
										Something went wrong. Please try again later.
									</p>
								)}
							</div>
						</form>
					</section>

					{/* Right Section - Location Info */}
					<aside className="space-y-8 pt-4">
						{/* Location Details Card */}
						<div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-gray-800">
							<h2 className="text-2xl font-americana font-bold mb-4 text-pink-600">
								Our Details
							</h2>

							<div className="space-y-5">
								{/* Address */}
								<div className="flex items-start">
									<span className="text-gray-800 mr-3 text-xl">&#9906;</span>
									<div>
										<h3 className="text-sm uppercase font-bold text-gray-700">
											Address
										</h3>
										<p className="text-gray-600 text-base leading-relaxed">
											18136 R. J.-A.-Bombardier,
											<br /> Mirabel, QC J7J 0H5, Canada
										</p>
									</div>
								</div>

								{/* Contact */}
								<div className="flex items-start">
									<span className="text-gray-800 mr-3 text-xl">&#x2709;</span>
									<div>
										<h3 className="text-sm uppercase font-bold text-gray-700">
											Contact
										</h3>
										<p className="text-gray-600 text-base">
											Phone:{" "}
											<span className="font-semibold text-gray-800">
												450-818-7078
											</span>
											<br />
											Email:{" "}
											<span className="font-semibold text-gray-800">
												info@MonsiniProm.com
											</span>
										</p>
									</div>
								</div>
							</div>
						</div>

						{/* Google Map Embed */}
						<div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
							<div className="w-full h-64 lg:h-80">
								<iframe
									title="Contact Location Map"
									className="w-full h-full border-0 grayscale hover:grayscale-0 transition-all duration-500"
									src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2793.316065600778!2d-73.92213!3d45.689148!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc8d6072757f5fb%3A0x99f44a4cd27f8672!2s18136%20R.%20J.-A.-Bombardier%2C%20Mirabel%2C%20QC%20J7J%200H5%2C%20Canada!5e0!3m2!1sen!2s!4v1696942945000!5m2!1sen!2s"
									allowFullScreen=""
									loading="lazy"></iframe>
							</div>
						</div>
					</aside>
				</div>
			</div>
			<Footer />
		</>
	);
}
